import React from "react";
import { Badge } from "components/ui/badge";
import { CallOutcome, InboundCall } from "./types";
import { OUTCOME_LABELS } from "./selectors";

const OUTCOME_CLASSES: Record<CallOutcome, string> = {
  ANSWERED_FROM_KNOWLEDGE: "bg-green-100 text-green-800 border-green-200",
  APPOINTMENT_REQUESTED: "bg-blue-100 text-blue-800 border-blue-200",
  FOLLOW_UP_NEEDED: "bg-amber-100 text-amber-800 border-amber-200",
  UNRESOLVED: "bg-red-100 text-red-800 border-red-200",
  APPOINTMENT_SCHEDULED: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

// A call only has an outcome once post-call processing finished; until then
// (or if it failed) the badge says where the call is instead of "Unresolved".
const STATUS_BADGES: Partial<
  Record<NonNullable<InboundCall["status"]>, { label: string; className: string }>
> = {
  IN_PROGRESS: {
    label: "In progress",
    className: "bg-sky-100 text-sky-800 border-sky-200",
  },
  PROCESSING: {
    label: "Processing",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  },
  FAILED: {
    label: "Processing failed",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  },
};

/** An emergency phrase was heard on the call (calls/inbound_agent.py is_emergency). */
export function UrgentBadge() {
  return (
    <Badge
      variant="outline"
      className="bg-red-100 text-red-800 border-red-200 text-[10px] px-1.5 py-0"
    >
      Urgent
    </Badge>
  );
}

/** True for rows whose outcome is real: finished calls, and fixtures that carry no status. */
export function hasOutcome(call: Pick<InboundCall, "status">): boolean {
  return call.status === undefined || call.status === "COMPLETED";
}

function OutcomeBadge({
  outcome,
  status,
}: {
  outcome: CallOutcome;
  status?: InboundCall["status"];
}) {
  const statusBadge = status === undefined ? undefined : STATUS_BADGES[status];
  if (statusBadge) {
    return (
      <Badge variant="outline" className={statusBadge.className}>
        {statusBadge.label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={OUTCOME_CLASSES[outcome]}>
      {OUTCOME_LABELS[outcome]}
    </Badge>
  );
}

export default OutcomeBadge;
